// SAR van ground-vehicle model (SAR-5, E-35). PURE + deterministic (ENG-1): an
// 8-seat truck the ground team drives to a search area. Throttle / brake / steer
// with rolling friction; it terrain-follows up to VAN_STEP blocks per move (a taller
// wall stops it), and in Earth mode is additionally kept on drivable columns
// (road / sidewalk / driveway) — off-road refuses the move. In Atlas mode there is
// no road layer yet, so `drivable` is absent and the van free-drives (graceful).
import { VAN } from '../config';

/** Steering the driver feeds the van each tick. */
export interface VanInput {
  throttle?: boolean;
  brake?: boolean;
  reverse?: boolean;
  /** Steer −1 (left) .. +1 (right). */
  steer?: number;
}

/** What the van needs from the world (ENG-1). `groundTop` is the surface block-Y a
 *  column follows; `drivable` (Earth only) gates the move to road/sidewalk/driveway. */
export interface VanWorld {
  /** Surface block-Y under a column (the block the wheels rest on top of). */
  groundTop(x: number, z: number): number;
  /** Earth-mode drivability (road/sidewalk/driveway); omit in Atlas → free-drive. */
  drivable?(x: number, z: number): boolean;
}

export interface Van {
  readonly id: number;
  x: number; y: number; z: number;
  yaw: number;
  /** Signed forward speed (blocks/tick); negative = reversing. */
  speed: number;
  onGround: boolean;
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

export class VanSystem {
  readonly vans: Van[] = [];
  private nextId = 1;

  get count(): number { return this.vans.length; }

  spawn(x: number, y: number, z: number, yaw = 0): Van {
    const v: Van = { id: this.nextId++, x, y, z, yaw, speed: 0, onGround: true };
    this.vans.push(v);
    return v;
  }

  remove(v: Van): void { const i = this.vans.indexOf(v); if (i >= 0) this.vans.splice(i, 1); }
  clear(): void { this.vans.length = 0; }

  tick(world: VanWorld, inputFor: (v: Van) => VanInput = () => ({})): void {
    for (const v of this.vans) stepVan(v, inputFor(v), world);
  }
}

/** Advance one van one tick (SAR-5). Exported for focused unit tests. */
export function stepVan(v: Van, input: VanInput, world: VanWorld): void {
  // ── longitudinal dynamics ───────────────────────────────────────────────────
  if (input.throttle) v.speed += input.reverse ? -VAN.ACCEL : VAN.ACCEL;
  if (input.brake) v.speed -= Math.sign(v.speed) * Math.min(Math.abs(v.speed), VAN.BRAKE);
  if (!input.throttle && !input.brake) v.speed *= VAN.FRICTION; // coast down
  v.speed = clamp(v.speed, -VAN.MAX_SPEED, VAN.MAX_SPEED);
  if (Math.abs(v.speed) < 1e-4) v.speed = 0;

  // Steering authority scales with how fast the van is rolling (parked → no turn).
  if (input.steer) v.yaw += input.steer * VAN.STEER_RATE * (v.speed / VAN.MAX_SPEED);

  if (v.speed === 0) { v.y = world.groundTop(v.x, v.z); v.onGround = true; return; }

  // ── proposed move along the heading (yaw 0 = −z) ────────────────────────────
  const nx = v.x + -Math.sin(v.yaw) * v.speed;
  const nz = v.z + -Math.cos(v.yaw) * v.speed;

  // Earth mode: refuse a move off a drivable column (E-35). Atlas → no constraint.
  if (world.drivable && !world.drivable(nx, nz)) { v.speed = 0; return; }

  // Terrain-follow: step up/down at most VAN_STEP; a taller wall stops the van.
  const here = world.groundTop(v.x, v.z);
  const there = world.groundTop(nx, nz);
  if (Math.abs(there - here) > VAN.STEP) { v.speed = 0; return; } // ≥2-block wall blocks it

  v.x = nx; v.z = nz; v.y = there; v.onGround = true;
}
